// src/components/Auth.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import './Auth.css';

// IMPORTANT: This redirect URI must EXACTLY match what you configured in Cognito
const REDIRECT_URI = 'https://financereportresourceanalytics.aws-practice.royalcyber.org/';
const CLIENT_ID = '7igsb66kgcot4427mrqrl6tg2r';
const CLIENT_SECRET = '';

// API endpoints
const ACCESS_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/access';
const DEPARTMENTS_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/departments';
const PROJECTS_API_URL = 'https://yv818kopca.execute-api.us-east-1.amazonaws.com/production/projects';

// Direct Microsoft login URL (bypassing Cognito UI)
const MICROSOFT_LOGIN_URL =
  `https://productionreport.auth.us-east-1.amazoncognito.com/oauth2/authorize?identity_provider=Microsoft&client_id=${CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;

const TOKEN_URL =
  'https://productionreport.auth.us-east-1.amazoncognito.com/oauth2/token';

export default function Auth() {
  const [, setLocation] = useLocation();
  const [error, setError] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Parse JWT token to get user email
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT', e);
      return {};
    }
  };

  // Check primary access list
  const checkPrimaryAccess = async (email) => {
    try {
      const response = await fetch(ACCESS_API_URL);
      
      if (!response.ok) {
        console.error('Failed to fetch primary access list:', response.status);
        throw new Error(`Failed to fetch primary access list: ${response.status}`);
      }
      
      const accessData = await response.json();
      
      // Check if the user's email is in the access list
      if (Array.isArray(accessData)) {
        for (const user of accessData) {
          if (user.email && user.email.toLowerCase() === email.toLowerCase()) {
            return { granted: true, isPrimaryUser: true };
          }
        }
      }
      
      return { granted: false, isPrimaryUser: false };
    } catch (error) {
      console.error('Error checking primary access:', error);
      throw error;
    }
  };

  // Check secondary access (departments lead_email)
  const checkSecondaryAccess = async (email) => {
    try {
      const response = await fetch(DEPARTMENTS_API_URL);
      
      if (!response.ok) {
        console.error('Failed to fetch departments list:', response.status);
        throw new Error(`Failed to fetch departments list: ${response.status}`);
      }
      
      const departmentsData = await response.json();
      
      // Check if user's email matches any lead_email in departments
      if (Array.isArray(departmentsData)) {
        for (const dept of departmentsData) {
          if (dept.lead_email && dept.lead_email.toLowerCase() === email.toLowerCase()) {
            // Store department name for filtering later
            return { 
              granted: true, 
              isPrimaryUser: false,
              department: dept.name || '' 
            };
          }
        }
      }
      
      return { granted: false, isPrimaryUser: false };
    } catch (error) {
      console.error('Error checking secondary access:', error);
      throw error;
    }
  };

  // Check tertiary access (projects delivery_manager_email)
  const checkTertiaryAccess = async (email) => {
    try {
      const response = await fetch(PROJECTS_API_URL);
      
      if (!response.ok) {
        console.error('Failed to fetch projects list:', response.status);
        throw new Error(`Failed to fetch projects list: ${response.status}`);
      }
      
      const projectsData = await response.json();
      
      // Check if user's email matches any delivery_manager_email in projects
      const managedProjectIds = [];
      if (Array.isArray(projectsData)) {
        projectsData.forEach(project => {
          if (project.delivery_manager_email && 
              project.delivery_manager_email.toLowerCase() === email.toLowerCase() &&
              project.id) {
            managedProjectIds.push(project.id);
          }
        });
      }
      
      if (managedProjectIds.length > 0) {
        // Store project IDs for filtering
        return {
          granted: true,
          isPrimaryUser: false,
          managedProjectIds
        };
      }
      
      return { granted: false, isPrimaryUser: false };
    } catch (error) {
      console.error('Error checking tertiary access:', error);
      throw error;
    }
  };

  // Combined access check
  const checkUserAccess = async (email) => {
    try {
      // First check primary access
      const primaryAccess = await checkPrimaryAccess(email);
      
      if (primaryAccess.granted) {
        localStorage.setItem('access_level', 'primary');
        return primaryAccess;
      }
      
      // If primary access failed, check secondary access
      const secondaryAccess = await checkSecondaryAccess(email);
      
      if (secondaryAccess.granted) {
        localStorage.setItem('access_level', 'secondary');
        localStorage.setItem('user_department', secondaryAccess.department);
        return secondaryAccess;
      }
      
      // If secondary access failed, check tertiary access
      const tertiaryAccess = await checkTertiaryAccess(email);
      
      if (tertiaryAccess.granted) {
        localStorage.setItem('access_level', 'tertiary');
        localStorage.setItem('managed_project_ids', JSON.stringify(tertiaryAccess.managedProjectIds));
        return tertiaryAccess;
      }
      
      // No access granted
      return { granted: false, isPrimaryUser: false };
    } catch (error) {
      console.error('Error in combined access check:', error);
      throw error;
    }
  };

  useEffect(() => {
    const handleAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const authError = params.get('error');

        // If there's a code in the URL, process it
        if (code) {
          setIsAuthenticating(true);
          setShowLoginPage(false);
          
          console.log('Code received, exchanging for token...');
          
          // Create form data for token exchange
          const formData = new URLSearchParams();
          formData.append('grant_type', 'authorization_code');
          formData.append('client_id', CLIENT_ID);
          formData.append('redirect_uri', REDIRECT_URI);
          formData.append('code', code);
          
          if (CLIENT_SECRET) {
            formData.append('client_secret', CLIENT_SECRET);
          }

          console.log('Request body:', formData.toString());
          
          const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
          };
          
          if (CLIENT_SECRET) {
            const base64Credentials = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
            headers['Authorization'] = `Basic ${base64Credentials}`;
          }
          
          const res = await fetch(TOKEN_URL, {
            method: 'POST',
            headers: headers,
            body: formData
          });

          const responseText = await res.text();
          
          if (!res.ok) {
            console.error('Token exchange failed:', res.status, responseText);
            setError(`Token exchange failed with status ${res.status}: ${responseText}`);
            setIsAuthenticating(false);
            return;
          }
          
          let tokenData;
          try {
            tokenData = JSON.parse(responseText);
            console.log('Token data received:', Object.keys(tokenData).join(', '));
          } catch (e) {
            console.error('Failed to parse token response:', e);
            setError('Failed to parse authentication response');
            setIsAuthenticating(false);
            return;
          }
          
          const { access_token, id_token, refresh_token } = tokenData;
          
          if (!access_token) {
            console.error('No access token in response');
            setError('No access token received');
            setIsAuthenticating(false);
            return;
          }
          
          // Get user email from id_token
          if (id_token) {
            const userData = parseJwt(id_token);
            const userEmail = userData.email;
            
            if (userEmail) {
              console.log('User email:', userEmail);
              localStorage.setItem('user_email', userEmail);
              
              try {
                // Check if the user has access (primary, secondary, or tertiary)
                const accessResult = await checkUserAccess(userEmail);
                
                if (!accessResult.granted) {
                  console.log('Access denied for user:', userEmail);
                  setAccessDenied(true);
                  setIsAuthenticating(false);
                  return;
                }
                
                console.log('Access granted for user:', userEmail, 
                  accessResult.isPrimaryUser ? '(Primary)' : '(Secondary/Tertiary)');
              } catch (accessError) {
                console.error('Error checking user access:', accessError);
                setError(`Error checking access: ${accessError.message}`);
                setIsAuthenticating(false);
                return;
              }
            } else {
              console.warn('No email found in ID token');
            }
          }
          
          localStorage.setItem('access_token', access_token);
          
          if (id_token) {
            localStorage.setItem('id_token', id_token);
          }
          
          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token);
          }
          
          console.log('Authentication successful, redirecting to dashboard...');
          
          setLocation('/dashboard');
          
          window.history.replaceState(null, '', '/dashboard');
        }
        else if (authError) {
          console.error('Authentication error:', authError);
          setError(`Authentication error: ${authError}`);
          setIsAuthenticating(false);
        }
        else if (localStorage.getItem('access_token')) {
          // Also check access for existing tokens
          const idToken = localStorage.getItem('id_token');
          if (idToken) {
            const userData = parseJwt(idToken);
            const userEmail = userData.email;
            
            if (userEmail) {
              localStorage.setItem('user_email', userEmail);
              try {
                const accessResult = await checkUserAccess(userEmail);
                if (!accessResult.granted) {
                  console.log('Access denied for stored session user:', userEmail);
                  setAccessDenied(true);
                  localStorage.removeItem('access_token');
                  localStorage.removeItem('id_token');
                  localStorage.removeItem('refresh_token');
                  localStorage.removeItem('access_level');
                  localStorage.removeItem('user_department');
                  localStorage.removeItem('managed_project_ids');
                  setShowLoginPage(true);
                  return;
                }
              } catch (accessError) {
                console.error('Error checking stored session user access:', accessError);
                setError(`Error checking access: ${accessError.message}`);
                return;
              }
            }
          }
          
          setLocation('/dashboard');
        }
        else {
          setShowLoginPage(true);
        }
      } catch (e) {
        console.error('Authentication error:', e);
        setError(`Authentication error: ${e.message}`);
        setIsAuthenticating(false);
      }
    };

    handleAuth();
  }, [setLocation]);

  // Handle Microsoft login button click - now using direct Microsoft URL
  const handleMicrosoftLogin = () => {
    setIsAuthenticating(true);
    // Redirect directly to Microsoft login
    window.location.href = MICROSOFT_LOGIN_URL;
  };

  // Show access denied page
  if (accessDenied) {
    return (
      <div className="auth-error-container">
        <div className="auth-error-box">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the dashboard. Please contact your administrator.</p>
          <button 
            onClick={() => {
              localStorage.removeItem('access_token');
              localStorage.removeItem('id_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('access_level');
              localStorage.removeItem('user_department');
              localStorage.removeItem('managed_project_ids');
              localStorage.removeItem('user_email');
              setAccessDenied(false);
              setShowLoginPage(true);
            }}
            className="try-again-btn"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Show custom login page
  if (showLoginPage && !isAuthenticating) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>Non-Billable Resource Status Report</h1>
            <p>Please sign in to access the dashboard</p>
          </div>
          
          <div className="login-body">
            <button 
              className="microsoft-login-btn"
              onClick={handleMicrosoftLogin}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="23" height="23">
                <path fill="#f1511b" d="M11.5 0h-11.5v11.5h11.5z"></path>
                <path fill="#80cc28" d="M23 0h-11.5v11.5h11.5z"></path>
                <path fill="#00adef" d="M11.5 11.5h-11.5v11.5h11.5z"></path>
                <path fill="#fbbc09" d="M23 11.5h-11.5v11.5h11.5z"></path>
              </svg>
              Sign in with Microsoft
            </button>
          </div>
          
          {error && <div className="login-error">{error}</div>}
        </div>
      </div>
    );
  }

  // Show loading spinner while authenticating
  return (
    <div className="auth-loading">
      <div className="loading-spinner"></div>
      <p>Logging you in...</p>
      {error && <div className="auth-error">{error}</div>}
    </div>
  );
}